import { ActionType, ProColumns, ProTable } from '@ant-design/pro-components';
import { useCompany } from '../../hooks/company';
import {
  BlackListItemDTO,
  CustomerPoolEnum,
  ListBlackListParams,
  blackListController,
  customerController,
} from '../../api';
import CustomerMobile from '../../components/CustomerMobile';
import CustomerChannelSelect from '../../components/CustomerChannelSelect';
import UserInfoModal from '../../components/UserInfoModal';
import dayjs from 'dayjs';
import '../../styles/common.scss';
import { Button, Popconfirm, Space, message } from 'antd';
import { UnorderedListOutlined } from '@ant-design/icons';
import { createContext, useContext, useRef, useState } from 'react';
import { useRequest } from 'ahooks';
import { LastEventDrawer } from '../../components/LastEvent';

const context = createContext<ActionType | undefined>(undefined);

const listContext = createContext<{
  setCustomerEvent?: (c: { customerId: I.ID; customerName: string }) => void;
  companyId?: I.ID;
}>({});

const Remove = ({ id }: { id: I.ID }) => {
  const actionRef = useContext(context);
  const { id: companyId, hasPermission } = useCompany();

  const { run: remove, loading } = useRequest(
    () => {
      return blackListController.remove(companyId, id);
    },
    {
      manual: true,
      onSuccess: () => {
        message.success('移除成功');
        actionRef?.reload();
      },
      onError: (error) => {
        message.error(error.message);
      },
    },
  );
  return (
    <Popconfirm title="确认删除" onConfirm={remove}>
      <Button
        disabled={!hasPermission('page:customer-list:blacklist:delete')}
        type="primary"
        size="small"
        danger
        loading={loading}
      >
        移除
      </Button>
    </Popconfirm>
  );
};

const Clear = ({ onlyAuto }: { onlyAuto: boolean }) => {
  const actionRef = useContext(context);
  const { id: companyId, hasPermission } = useCompany();
  const { run: clear, loading } = useRequest(
    () => {
      return blackListController.empty(companyId, onlyAuto);
    },
    {
      manual: true,
      onSuccess: () => {
        message.success('清空成功');
        actionRef?.reload();
      },
      onError: (error) => {
        message.error(error.message);
      },
    },
  );
  return (
    <Popconfirm title="确认清空，清空后无法恢复" onConfirm={clear}>
      <Button
        disabled={!hasPermission('page:customer-list:blacklist:delete')}
        type="primary"
        size="small"
        danger
        loading={loading}
      >
        {onlyAuto ? '清空自动拉黑' : '清空全部拉黑'}
      </Button>
    </Popconfirm>
  );
};

const MobileCell = ({ mobile }: { mobile: string }) => {
  const { companyId, setCustomerEvent } = useContext(listContext);
  const { runAsync: fetchCustomer, loading } = useRequest(
    () => {
      return customerController.getDetailByMobile(companyId!, mobile);
    },
    {
      manual: true,
    },
  );
  return (
    <Space>
      <CustomerMobile mobile={mobile} />
      <Button
        size="small"
        loading={loading}
        icon={<UnorderedListOutlined />}
        onClick={async () => {
          const customer = await fetchCustomer();
          if (customer) {
            setCustomerEvent?.({
              customerId: customer.id,
              customerName: customer.name,
            });
          } else {
            message.info('客户不存在');
          }
        }}
      />
    </Space>
  );
};

const columns: ProColumns<BlackListItemDTO>[] = [
  {
    dataIndex: 'id',
    search: false,
    title: 'id',
  },
  {
    dataIndex: 'name',
    search: false,
    title: '姓名',
  },
  {
    dataIndex: 'mobile',
    title: '手机号码',
    hideInSearch: true,
    render: (_, { mobile }) => {
      return <MobileCell mobile={mobile} />;
    },
  },
  {
    hideInTable: true,
    title: '客户信息',
    dataIndex: 'keyword',
    search: {
      transform: (keyword) => {
        return {
          keyword,
        };
      },
    },
    fieldProps: {
      placeholder: '手机号码或者客户姓名',
    },
  },
  {
    dataIndex: 'channel',
    renderFormItem(schema, config, form, action) {
      return <CustomerChannelSelect />;
    },
    title: '渠道',
  },
  {
    dataIndex: 'remark',
    search: false,
    title: '备注',
  },
  {
    title: '操作人',
    dataIndex: 'userId',
    search: false,
    render: (_, { remark, user }) => {
      if (user) {
        return <UserInfoModal id={user.id} name={user.name} />;
      }

      return <span>{remark}</span>;
    },
  },
  {
    title: '操作时间',
    dataIndex: 'createdAt',
    valueType: 'dateRange',
    search: {
      transform: (value) => {
        return {
          createdAt: [dayjs(value[0]), dayjs(value[1])],
        };
      },
    },
    render: (_, { createdAt }) =>
      dayjs(createdAt).format('YYYY-MM-DD HH:mm:ss'),
  },
  {
    title: '操作',
    hideInForm: true,
    hideInSearch: true,
    render: (_, { id }) => {
      return <Remove id={id} />;
    },
  },
];

export default () => {
  const { id: companyId } = useCompany();
  const [selectedCustomerEvent, setSelectedCustomerEvent] = useState<{
    customerId: I.ID;
    customerName: string;
  }>();
  const ref = useRef<ActionType>();
  return (
    <>
      <listContext.Provider
        value={{ setCustomerEvent: setSelectedCustomerEvent, companyId }}
      >
        <context.Provider value={ref.current}>
          <ProTable<BlackListItemDTO, ListBlackListParams & { companyId: I.ID }>
            className="pro-table"
            actionRef={ref}
            rowKey="id"
            columns={columns}
            params={{
              companyId,
            }}
            pagination={{
              pageSizeOptions: [20, 50, 100, 200, 500, 1000],
            }}
            size="small"
            bordered
            toolBarRender={() => [
              <Clear key="clear" onlyAuto={false} />,
              <Clear key="clear-auto" onlyAuto={true} />,
            ]}
            cardBordered={{ search: true }}
            dateFormatter="number"
            request={async (
              { current, pageSize, companyId, ...params },
              sort,
              filter,
            ) => {
              return blackListController
                .list(companyId, current, pageSize, params)
                .then((resp) => ({
                  data: resp.list,
                  success: true,
                  total: resp.total,
                }));
            }}
          />
        </context.Provider>
      </listContext.Provider>
      {selectedCustomerEvent && (
        <LastEventDrawer
          pool={CustomerPoolEnum.PUBLIC}
          open={!!selectedCustomerEvent}
          onCancel={() => setSelectedCustomerEvent(undefined)}
          customerId={selectedCustomerEvent.customerId}
          customerName={selectedCustomerEvent.customerName}
        />
      )}
    </>
  );
};
